import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { User } from '../../models/models';
import {
	LoginResponse,
	PortalRedirectInfo,
	TwoFactorResponse,
	TwoFactorVerifyRequest,
	TwoFactorChannel,
	TwoFactorChannelState,
	TwoFactorSendResponse,
	TwoFactorSendError,
	PortalType,
	PasswordResetResponse,
	PasswordResetData,
	ForceResetPasswordRequest
} from '../../models/auth.model';
import { PortalDetectionService } from '../../services/portal-detection.service';
import { PendingChangesService } from '../../services/pending-changes.service';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private loggedInUser: User | null = null;
	private roles: string[] = [];
	private permissions: string[] = [];
	private rolesPermissions$?: Observable<any>;

	baseUrl: string = environment.apiUrl;
	// Extract base domain from apiUrl for Sanctum endpoints
	private sanctumBaseUrl: string = environment.apiUrl.replace(/\/api\/v1\/?$/, '');

	constructor(
		private http: HttpClient,
		private portalDetection: PortalDetectionService,
		private pendingChanges: PendingChangesService
	) {}

	login(email: string, password: string): Observable<LoginResponse | TwoFactorResponse | PasswordResetResponse> {
		// Step 1: Get CSRF cookie from Sanctum
		return this.http.get(`${this.sanctumBaseUrl}/sanctum/csrf-cookie`, { withCredentials: true }).pipe(
			switchMap(() => {
				// Step 2: Login (session cookie set automatically by browser)
				const body = {
					email: email,
					password: password,
					portal: 'company'
				};
				return this.http.post<LoginResponse | TwoFactorResponse | PasswordResetResponse>(
					`${this.sanctumBaseUrl}/api/v1/login`,
					body,
					{
						withCredentials: true
					}
				);
			}),
			tap((res) => {
				if (this.isPasswordResetResponse(res)) {
					this.storePasswordResetSession(email, res.temporaryToken, res.portal, res.expiresIn);
				} else if (this.isTwoFactorResponse(res)) {
					this.storeTwoFactorSession(email, res);
				} else if (this.isLoginResponse(res)) {
					this.persistLogin(res);
				}
			}),
			catchError((err: HttpErrorResponse) => {
				return this.handleLoginError(err);
			})
		);
	}

	logout(): Observable<any> {
		this.roles = [];
		this.permissions = [];
		this.loggedInUser = null;
		this.invalidateRolesPermissions();
		localStorage.removeItem('roles');
		localStorage.removeItem('permissions');
		localStorage.removeItem('loggedInUser');
		sessionStorage.removeItem('redirect_after_login');

		return this.http.post(`${this.baseUrl}auth/logout`, {}, { withCredentials: true });
	}

	isLoggedIn(): boolean {
		// Since session is managed by browser cookies, check if user data exists
		return !!localStorage.getItem('loggedInUser');
	}

	setLoggedInUser(loggedInUser: User): void {
		this.loggedInUser = loggedInUser;
		localStorage.setItem('loggedInUser', JSON.stringify(loggedInUser));
	}

	getLoggedInUser(): User | null {
		const user = localStorage.getItem('loggedInUser');
		if (user) {
			this.loggedInUser = JSON.parse(user);
			return this.loggedInUser;
		}
		return null;
	}

	getUserFullName(): string {
		return `${this.loggedInUser?.firstname} ${this.loggedInUser?.lastname}`;
	}

	setRoles(roles: string[]): void {
		this.roles = roles;
		localStorage.setItem('roles', JSON.stringify(roles));
	}

	getRoles(): string[] {
		if (this.roles.length > 0) {
			return this.roles;
		}
		const storedRoles = localStorage.getItem('roles');
		return storedRoles ? JSON.parse(storedRoles) : [];
	}

	setPermissions(permissions: string[]): void {
		this.permissions = permissions;
		localStorage.setItem('permissions', JSON.stringify(permissions));
	}

	getPermissions(): string[] {
		if (this.permissions.length > 0) {
			return this.permissions;
		}
		const storedPermissions = localStorage.getItem('permissions');
		return storedPermissions ? JSON.parse(storedPermissions) : [];
	}

	hasPermission(permission: string): boolean {
		return this.getPermissions().includes(permission);
	}

	isAdmin(): boolean {
		return this.getRoles().includes('test_test');
	}

	handleUnauthorized(): void {
		const currentUrl = window.location.pathname + window.location.search;
		if (currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/auth')) {
			sessionStorage.setItem('redirect_after_login', currentUrl);
		}

		localStorage.removeItem('roles');
		localStorage.removeItem('permissions');
		localStorage.removeItem('loggedInUser');
		this.roles = [];
		this.permissions = [];
		this.loggedInUser = null;
		this.invalidateRolesPermissions();
		this.pendingChanges.discardAll();
		window.location.href = '/auth/login';
	}

	getUserRolesAndPermissions(): Observable<any> {
		if (!this.rolesPermissions$) {
			this.rolesPermissions$ = this.http
				.get(`${this.baseUrl}user/roles-permissions`, { withCredentials: true })
				.pipe(shareReplay(1));
		}
		return this.rolesPermissions$;
	}

	invalidateRolesPermissions(): void {
		this.rolesPermissions$ = undefined;
	}

	// Two-Factor Authentication methods
	verifyTwoFactor(email: string, code: string, temporaryToken: string, portal: PortalType): Observable<any> {
		const body: TwoFactorVerifyRequest = {
			email: email,
			code: code,
			temporary_token: temporaryToken,
			portal
		};
		return this.http.post<LoginResponse>(`${this.baseUrl}two-factor/verify`, body, { withCredentials: true }).pipe(
			tap((res) => {
				// Session cookie is set automatically by browser after successful 2FA
				if (res?.status) {
					this.persistLogin(res);
					this.clearTwoFactorSession();
				}
			}),
			catchError((err: HttpErrorResponse) => {
				return this.handleLoginError(err);
			})
		);
	}

	sendTwoFactorCode(
		email: string,
		temporaryToken: string,
		portal: PortalType,
		channel?: TwoFactorChannel
	): Observable<TwoFactorSendResponse> {
		const body: Record<string, string> = {
			email: email,
			temporary_token: temporaryToken,
			portal
		};
		if (channel) {
			body['channel'] = channel;
		}
		return this.http.post<any>(`${this.baseUrl}two-factor/send`, body, { withCredentials: true }).pipe(
			map(
				(res): TwoFactorSendResponse => ({
					success: !!res?.success,
					message: res?.message ?? '',
					expiresIn: res?.expires_in ?? 5,
					retryAfter: res?.retry_after ?? 0,
					channel: res?.channel ?? null,
					destination: res?.destination ?? null
				})
			),
			catchError((err: HttpErrorResponse) => {
				const retryAfter = err.error?.retry_after;
				const sendError: TwoFactorSendError = {
					status: err.status,
					code: err.error?.code ?? null,
					message: err.error?.message || 'Failed to send verification code',
					retryAfter: typeof retryAfter === 'number' ? retryAfter : null
				};
				return throwError(() => sendError);
			})
		);
	}

	getTwoFactorChannelState(temporaryToken: string): TwoFactorChannelState | null {
		const raw = sessionStorage.getItem('temp_2fa_channels');
		if (!raw) {
			return null;
		}
		try {
			const state = JSON.parse(raw) as TwoFactorChannelState;
			// Ignore leftovers from an earlier login attempt in this tab.
			return state.temporaryToken === temporaryToken ? state : null;
		} catch {
			return null;
		}
	}

	private isPasswordResetResponse(
		response: LoginResponse | TwoFactorResponse | PasswordResetResponse
	): response is PasswordResetResponse {
		return (response as PasswordResetResponse)?.requiresPasswordReset === true;
	}

	private isTwoFactorResponse(
		response: LoginResponse | TwoFactorResponse | PasswordResetResponse
	): response is TwoFactorResponse {
		return (response as TwoFactorResponse)?.requiresTwoFactor === true;
	}

	private isLoginResponse(
		response: LoginResponse | TwoFactorResponse | PasswordResetResponse
	): response is LoginResponse {
		// With Sanctum, successful login returns status: true with user data (no accessToken for SPA)
		return (response as LoginResponse)?.status === true && !!(response as LoginResponse)?.data?.loggedInUser;
	}

	private persistLogin(response: LoginResponse) {
		// No token to store - session cookie is managed by browser
		this.invalidateRolesPermissions();
		this.setRoles(response.data.roles);
		this.setPermissions(response.data.permissions);
		this.setLoggedInUser(response.data.loggedInUser);
	}

	private storeTwoFactorSession(email: string, res: TwoFactorResponse) {
		sessionStorage.setItem('temp_2fa_email', email);
		sessionStorage.setItem('temp_2fa_token', res.temporaryToken);
		sessionStorage.setItem('temp_2fa_portal', res.portal);

		const channelState: TwoFactorChannelState = {
			temporaryToken: res.temporaryToken,
			codeSent: res.codeSent !== false,
			sentChannel: res.twoFactorChannel ?? (res.codeSent === false ? null : 'email'),
			defaultChannel: res.twoFactorDefaultChannel ?? 'email',
			channels: res.twoFactorChannels ?? []
		};
		sessionStorage.setItem('temp_2fa_channels', JSON.stringify(channelState));
	}

	private clearTwoFactorSession() {
		sessionStorage.removeItem('temp_2fa_email');
		sessionStorage.removeItem('temp_2fa_token');
		sessionStorage.removeItem('temp_2fa_portal');
		sessionStorage.removeItem('temp_2fa_channels');
	}

	// Password Reset Session methods
	private storePasswordResetSession(email: string, token: string, portal: PortalType, expiresIn: number = 5) {
		sessionStorage.setItem('temp_reset_email', email);
		sessionStorage.setItem('temp_reset_token', token);
		sessionStorage.setItem('temp_reset_portal', portal);
		sessionStorage.setItem('temp_reset_expires_in', expiresIn.toString());
	}

	getPasswordResetData(): PasswordResetData | null {
		const email = sessionStorage.getItem('temp_reset_email');
		const temporaryToken = sessionStorage.getItem('temp_reset_token');
		const portal = sessionStorage.getItem('temp_reset_portal') as PortalType;
		const expiresInStr = sessionStorage.getItem('temp_reset_expires_in');

		if (!email || !temporaryToken || !portal) {
			return null;
		}

		const expiresIn = expiresInStr ? parseInt(expiresInStr, 10) : 5;

		return { email, temporaryToken, portal, expiresIn };
	}

	clearPasswordResetSession() {
		sessionStorage.removeItem('temp_reset_email');
		sessionStorage.removeItem('temp_reset_token');
		sessionStorage.removeItem('temp_reset_portal');
		sessionStorage.removeItem('temp_reset_expires_in');
	}

	forgotPassword(email: string): Observable<any> {
		return this.http
			.get(`${this.sanctumBaseUrl}/sanctum/csrf-cookie`, { withCredentials: true })
			.pipe(
				switchMap(() =>
					this.http.post(
						`${this.baseUrl}forgot-password`,
						{ email, portal: 'company' },
						{ withCredentials: true }
					)
				)
			);
	}

	resetPassword(token: string, email: string, password: string, passwordConfirmation: string): Observable<any> {
		return this.http.get(`${this.sanctumBaseUrl}/sanctum/csrf-cookie`, { withCredentials: true }).pipe(
			switchMap(() =>
				this.http.post(
					`${this.baseUrl}reset-password`,
					{ token, email, password, password_confirmation: passwordConfirmation, portal: 'company' },
					{ withCredentials: true }
				)
			),
			catchError((err: HttpErrorResponse) => throwError(() => err))
		);
	}

	forceResetPassword(request: ForceResetPasswordRequest): Observable<LoginResponse | TwoFactorResponse> {
		return this.http
			.post<LoginResponse | TwoFactorResponse>(`${this.baseUrl}force-reset-password`, request, {
				withCredentials: true
			})
			.pipe(
				tap((res) => {
					if (this.isTwoFactorResponse(res)) {
						// Password reset successful but user has 2FA - store new temp token
						this.storeTwoFactorSession(request.email, res);
						this.clearPasswordResetSession();
					} else if (this.isLoginResponse(res)) {
						// Password reset and login complete
						this.persistLogin(res);
						this.clearPasswordResetSession();
					}
				}),
				catchError((err: HttpErrorResponse) => {
					return throwError(() => err);
				})
			);
	}

	private handleLoginError(error: HttpErrorResponse) {
		if (error.status === 403 && error.error?.redirect) {
			const redirectInfo = error.error.redirect as PortalRedirectInfo;
			this.handlePortalMismatch(redirectInfo);
			return throwError(() => new Error('portal_mismatch'));
		}
		return throwError(() => error);
	}

	private handlePortalMismatch(redirectInfo: PortalRedirectInfo) {
		const redirectData = {
			reason: redirectInfo.reason,
			userType: redirectInfo.userType,
			fromPortal: 'company',
			timestamp: Date.now()
		};
		sessionStorage.setItem('redirect_info', JSON.stringify(redirectData));
		const targetUrl = this.portalDetection.getPortalUrl(redirectInfo.targetPortal);
		this.pendingChanges.discardAll();
		window.location.href = `${targetUrl}/login`;
	}
}
